import { ControlDocuments, evidenceDocumentIds } from '@/components/evidence/ControlDocuments';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Add01Icon,
  AiBrain01Icon,
  ArrowLeft01Icon,
  Building01Icon,
  Calendar03Icon,
  CancelCircleIcon,
  ChartUpIcon,
  CheckmarkCircle02Icon,
  Clock01Icon,
  Coins01Icon,
  Database01Icon,
  Delete02Icon,
  Download01Icon,
  FileAttachmentIcon,
  Location01Icon,
  Mail01Icon,
  MoreHorizontalIcon,
  Notification01Icon,
  PauseIcon,
  PencilIcon,
  Radar01Icon,
  Refresh01Icon,
  SecurityCheckIcon,
  SentIcon,
  StethoscopeIcon,
  StopIcon,
  Tag01Icon,
  Task01Icon,
  Tick02Icon,
  Time01Icon,
  UserAccountIcon,
  UserGroupIcon,
  UserMultipleIcon,
  UserSettings01Icon,
  WorkHistoryIcon,
} from '@hugeicons/core-free-icons';
import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router';
import { api, apiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { UserPicker } from '@/components/common/UserPicker';
import { InlineEdit } from '@/components/common/InlineEdit';
import { IntegrationTags } from '@/components/common/IntegrationTags';
import { InlineOwnerEdit } from '@/components/common/InlineOwnerEdit';
import {
  RiskBadge,
  StageBadge,
  StatusBadge,
  EmptyState,
  EmptyExplainer,
  PriorityBadge,
} from '@/components/common/Bits';
import { fmtDate, fmtDateTime, DOC_TYPES, METRIC_TYPES, REEVAL_TRIGGERS } from '@/lib/constants';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { BenefitsSection, MilestonesSection, parseList } from '@/components/initiative/InitiativeTracking';

// MetaLabel reserva una altura mínima de dos líneas para que, aunque un label
// haga wrap (p. ej. "Región de procesamiento"), el valor de todas las celdas de
// la misma fila arranque a la misma altura y la grilla se vea simétrica.
const MetaLabel = ({ icon, label }) => (
  <p className="flex items-start gap-1.5 min-h-[2.1em] text-[11px] leading-tight uppercase tracking-wider text-muted-foreground font-medium">
    {icon && <HugeiconsIcon icon={icon} size={13} strokeWidth={1.5} className="mt-px shrink-0 opacity-70" />}
    <span>{label}</span>
  </p>
);

const Meta = ({ label, value, icon }) => (
  <div>
    <MetaLabel icon={icon} label={label} />
    <p className="text-sm mt-1 font-medium">{value ?? '—'}</p>
  </div>
);

const OwnerMeta = ({ label, value, initiativeName }) => {
  const match = value?.match(/^(.+?)\s*<(.+?)>$/);
  const name = match ? match[1].trim() : value;
  const email = match ? match[2] : null;

  if (!value || !email) {
    return label ? <Meta label={label} value={name} /> : <p className="text-sm mt-1 font-medium">{name || '—'}</p>;
  }

  return (
    <div>
      {label && <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{label}</p>}
      <HoverCard openDelay={200} closeDelay={100}>
        <HoverCardTrigger asChild>
          <p className="text-sm mt-1 font-medium cursor-default underline decoration-dotted decoration-muted-foreground/40 underline-offset-2 w-fit">
            {name}
          </p>
        </HoverCardTrigger>
        <HoverCardContent className="w-72 p-4" align="start">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0">
                {name.slice(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{name}</p>
                <p className="text-xs text-muted-foreground truncate">{email}</p>
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1.5 flex-1"
                onClick={() => window.open(`mailto:${email}?subject=${initiativeName || ''}`, '_blank')}>
                <HugeiconsIcon icon={Mail01Icon} className="h-3 w-3" /> Correo
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1.5 flex-1"
                onClick={async () => {
                  toast.success(`Recordatorio enviado a ${name}`);
                }}>
                <HugeiconsIcon icon={Notification01Icon} className="h-3 w-3" /> Recordatorio
              </Button>
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>
    </div>
  );
};

const InlineSelect = ({ value, options, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const handleChange = async (v) => {
    if (v === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(v);
      setEditing(false);
    } catch {
    } finally {
      setSaving(false);
    }
  };
  if (!editing) {
    return (
      <div
        onDoubleClick={() => setEditing(true)}
        className="cursor-default group"
        title="Doble clic para editar"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter') setEditing(true);
        }}>
        <span className="text-sm mt-1 font-medium group-hover:bg-primary/5 group-hover:rounded px-0.5 -mx-0.5 transition-colors">
          {value || '—'}
        </span>
      </div>
    );
  }
  return (
    <Select value={value} onValueChange={handleChange} disabled={saving}>
      <SelectTrigger className="h-7 text-sm">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o} value={o}>
            {o}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const InlineToggle = ({ value, onSave }) => {
  const [saving, setSaving] = useState(false);
  const toggle = async () => {
    setSaving(true);
    try {
      await onSave(!value);
    } catch {
    } finally {
      setSaving(false);
    }
  };
  return (
    <button
      onClick={toggle}
      disabled={saving}
      className={cn(
        'inline-block text-sm mt-1 font-medium px-2 py-0.5 rounded transition-colors cursor-pointer',
        value ? 'bg-accent/10 text-accent' : 'bg-muted text-muted-foreground hover:bg-muted/80',
      )}
      title="Clic para cambiar"
      aria-label={value ? 'Desactivar' : 'Activar'}>
      {value ? 'Sí' : 'No'}
    </button>
  );
};

export default function InitiativeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { can } = useAuth();
  // El tab activo se refleja en la URL (?tab=) para que se conserve al recargar
  // y para que los enlaces sean compartibles. Por defecto: "evaluaciones".
  const TAB_VALUES = [
    'evaluaciones',
    'beneficios',
    'decisiones',
    'controles',
    'evidencias',
    'metricas',
    'hitos',
    'inventario',
    'historial',
  ];
  const rawTab = searchParams.get('tab');
  const activeTab = TAB_VALUES.includes(rawTab) ? rawTab : 'evaluaciones';
  const onTabChange = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'evaluaciones') next.delete('tab');
    else next.set('tab', value);
    setSearchParams(next, { replace: true });
  };
  const [d, setD] = useState(null);
  const [catalogs, setCatalogs] = useState(null);
  const [dossier, setDossier] = useState(null);
  const [capabilities, setCapabilities] = useState([]);
  const [relationship, setRelationship] = useState(null);
  const [triage, setTriage] = useState(null);
  const [editingCreator, setEditingCreator] = useState(false);

  const load = useCallback(async () => {
    const [{ data }, { data: check }] = await Promise.all([
      api.get(`/initiatives/${id}`),
      api.get(`/initiatives/${id}/dossier-check`),
    ]);
    setD(data);
    setDossier(check);
    // Load capabilities, relationship, triage (non-blocking)
    api
      .get(`/iniciativas/${id}/capacidades`)
      .then(({ data }) => setCapabilities(data || []))
      .catch(() => {});
    api
      .get('/relaciones-negocio')
      .then(({ data }) => {
        const rel = (data || []).find((r) => r.initiative_id === id);
        setRelationship(rel || null);
      })
      .catch(() => {});
    api
      .get(`/iniciativas/${id}/triage`)
      .then(({ data }) => setTriage(data || null))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    load().catch(() => toast.error('No fue posible cargar la iniciativa'));
    api.get('/catalogs').then(({ data }) => setCatalogs(data));
  }, [load]);

  if (!d)
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );

  const ini = d.initiative;

  const submitCommittee = async () => {
    try {
      await api.post(`/initiatives/${id}/submit`);
      toast.success('Iniciativa enviada al Comité de IA');
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10 space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(-1)}
        className="text-muted-foreground -ml-2"
        data-testid="back-btn">
        <HugeiconsIcon icon={ArrowLeft01Icon} className="h-4 w-4 mr-1" /> Volver
      </Button>

      {/* Header */}
      <div
        className="bg-card rounded-xl border border-border shadow-card p-6 sm:p-8 animate-in-up"
        data-testid="initiative-header">
        <div className="flex flex-col lg:flex-row lg:items-start gap-6">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="font-mono">{ini.code}</span> · <span>{ini.asset_type}</span> ·{' '}
              <span>{ini.department}</span>
              {ini.from_shadow_ai && (
                <Badge variant="outline" className="bg-risk-medhigh/10 text-risk-medhigh border-risk-medhigh/25">
                  <HugeiconsIcon icon={Radar01Icon} className="h-3 w-3 mr-1" /> Origen Shadow AI
                </Badge>
              )}
            </div>
            {can.edit ? (
              <InlineEdit
                value={ini.name}
                placeholder="Nombre de la iniciativa"
                className="mt-2"
                onSave={async (v) => {
                  const next = (v || '').trim();
                  if (!next) {
                    toast.error('El nombre de la iniciativa no puede quedar vacío');
                    throw new Error('empty-name');
                  }
                  if (next === ini.name) return;
                  await api.put(`/initiatives/${id}`, { data: { name: next }, reason: 'Cambio de nombre' });
                  load();
                }}>
                <h1 className="font-display text-2xl sm:text-3xl font-semibold leading-tight group-hover:bg-primary/5 group-hover:rounded px-0.5 -mx-0.5 transition-colors">
                  {ini.name}
                </h1>
              </InlineEdit>
            ) : (
              <h1 className="font-display text-2xl sm:text-3xl font-semibold mt-2 leading-tight">{ini.name}</h1>
            )}
            <div className="flex flex-wrap gap-2 mt-4">
              <StageBadge stage={ini.stage} />
              <RiskBadge level={ini.residual_risk} />
              <StatusBadge status={ini.committee_status} />
              <StatusBadge status={ini.evaluation_status} />
              {ini.health_status && ini.health_status !== 'Sin actualización' && (
                <Badge
                  variant="outline"
                  className={cn(
                    'text-xs',
                    ini.health_status === 'Verde' && 'bg-emerald-500/10 text-emerald-600 border-emerald-500/25',
                    ini.health_status === 'Amarillo' && 'bg-amber-500/10 text-amber-600 border-amber-500/25',
                    ini.health_status === 'Rojo' && 'bg-rose-500/10 text-rose-600 border-rose-500/25',
                    ini.health_status === 'Naranja' && 'bg-orange-500/10 text-orange-600 border-orange-500/25',
                  )}>
                  Salud: {ini.health_status}
                </Badge>
              )}
              {ini.horizon && (
                <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/25 text-xs">
                  {ini.horizon}
                </Badge>
              )}
            </div>
          </div>
          {can.edit && (
            <TooltipProvider delayDuration={300}>
              <div className="flex flex-wrap gap-2 shrink-0">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button asChild variant="outline" className="border-border" data-testid="evaluate-btn">
                      <Link to={`/iniciativas/${id}/evaluar`}>
                        <HugeiconsIcon icon={SecurityCheckIcon} className="mr-2 h-4 w-4" /> Evaluar riesgo
                      </Link>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    Ejecuta el cuestionario de 15 dimensiones de riesgo. Calcula el score inherente/residual, determina
                    controles obligatorios y asigna revisores.
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0}>
                      <Button
                        onClick={submitCommittee}
                        disabled={!dossier?.complete || ini.committee_status === 'En revisión'}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground"
                        data-testid="submit-committee-btn">
                        <HugeiconsIcon icon={SentIcon} className="h-4 w-4 mr-2" /> Enviar al Comité
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    {dossier?.complete
                      ? 'Envía la iniciativa al Comité de IA para votación. Se abre una ronda que requiere el quórum de miembros configurado (mayoría del comité) para decidir si aprobar, postergar o rechazar.'
                      : `No se puede enviar aún. Para que el Comité pueda decidir necesita: ${dossier?.missing?.join(', ') || 'evaluación de riesgo, al menos un control asignado y un documento de evidencia'}.`}
                  </TooltipContent>
                </Tooltip>
                <EditDialog ini={ini} catalogs={catalogs} onSaved={load} />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0}>
                      <ReevalDialog ini={ini} onSaved={load} />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    Activa una reevaluación cuando cambian las condiciones (nuevo modelo, incidente, cambio
                    regulatorio). Conserva el riesgo anterior y genera una nueva versión.
                  </TooltipContent>
                </Tooltip>

                {/* More actions dropdown: Pause, Archive, Delete */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" className="border-border" aria-label="Más acciones">
                      <HugeiconsIcon icon={MoreHorizontalIcon} className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {ini.stage !== 'Pausada' && ini.stage !== 'Archivada' && (
                      <DropdownMenuItem
                        onClick={async () => {
                          try {
                            await api.put(`/initiatives/${id}`, {
                              data: { stage: 'Pausada' },
                              reason: 'Iniciativa pausada',
                            });
                            toast.success('Iniciativa pausada');
                            load();
                          } catch (e) {
                            toast.error(apiError(e));
                          }
                        }}
                        className="gap-2 cursor-pointer">
                        <HugeiconsIcon icon={PauseIcon} className="h-4 w-4 text-muted-foreground" />
                        Pausar
                      </DropdownMenuItem>
                    )}
                    {(ini.stage === 'Pausada' || ini.stage === 'Archivada') && (
                      <DropdownMenuItem
                        onClick={async () => {
                          try {
                            await api.put(`/initiatives/${id}`, {
                              data: { stage: 'Idea', evaluation_status: 'Pendiente', committee_status: 'Pendiente' },
                              reason: 'Devuelta a bandeja de entrada',
                            });
                            toast.success('Devuelta a bandeja');
                            load();
                          } catch (e) {
                            toast.error(apiError(e));
                          }
                        }}
                        className="gap-2 cursor-pointer">
                        <HugeiconsIcon icon={Refresh01Icon} className="h-4 w-4 text-muted-foreground" />
                        Devolver a bandeja
                      </DropdownMenuItem>
                    )}
                    {ini.stage !== 'Archivada' && (
                      <DropdownMenuItem
                        onClick={async () => {
                          try {
                            await api.put(`/initiatives/${id}`, {
                              data: { stage: 'Archivada' },
                              reason: 'Iniciativa archivada',
                            });
                            toast.success('Iniciativa archivada');
                            load();
                          } catch (e) {
                            toast.error(apiError(e));
                          }
                        }}
                        className="gap-2 cursor-pointer">
                        <HugeiconsIcon icon={StopIcon} className="h-4 w-4 text-muted-foreground" />
                        Archivar
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <DropdownMenuItem
                          onSelect={(e) => e.preventDefault()}
                          className="gap-2 cursor-pointer text-destructive focus:text-destructive">
                          <HugeiconsIcon icon={Delete02Icon} className="h-4 w-4" />
                          Eliminar
                        </DropdownMenuItem>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle className="font-display">Eliminar iniciativa</AlertDialogTitle>
                          <AlertDialogDescription>
                            ¿Está seguro que desea eliminar{' '}
                            <span className="font-medium text-foreground">{ini.name}</span>? Se marcará como eliminada
                            pero se conservará en la auditoría. Esta acción no se puede deshacer.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={async () => {
                              try {
                                await api.put(`/initiatives/${id}`, {
                                  data: { stage: 'Archivada', deleted_at: new Date().toISOString() },
                                  reason: 'Iniciativa eliminada',
                                });
                                toast.success('Iniciativa eliminada');
                                navigate('/registro');
                              } catch (e) {
                                toast.error(apiError(e));
                              }
                            }}>
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </TooltipProvider>
          )}
        </div>

        {ini.description && <p className="text-sm text-muted-foreground mt-4 leading-relaxed">{ini.description}</p>}

        {dossier && !dossier.complete && (
          <div
            className="mt-5 flex flex-col gap-2 rounded-lg border border-risk-medium/30 bg-risk-medium/5 px-4 py-3 sm:flex-row sm:items-center sm:gap-3"
            data-testid="dossier-warning">
            <p className="text-xs font-semibold text-risk-medium shrink-0">Faltan requisitos para enviar al Comité</p>
            <div className="flex flex-wrap gap-1.5">
              {dossier.missing.map((m) => (
                <span
                  key={m}
                  className="inline-flex items-center rounded-full border border-risk-medium/25 bg-risk-medium/10 px-2.5 py-0.5 text-[11px] font-medium text-risk-medium">
                  {m}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ── Grupo: Responsables (personas) ── */}
        <div className="mt-6 pt-6 border-t border-border">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80 mb-3">
            Responsables
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4 auto-rows-min items-start">
            <div>
              <MetaLabel icon={UserAccountIcon} label="Resp. de negocio" />
              <InlineOwnerEdit
                value={ini.business_owner}
                onSave={async (v) => {
                  await api.put(`/initiatives/${id}`, {
                    data: { business_owner: v },
                    reason: 'Cambio de responsable de negocio',
                  });
                  load();
                }}>
                <OwnerMeta label="" value={ini.business_owner} initiativeName={ini.name} />
              </InlineOwnerEdit>
            </div>
            <div>
              <MetaLabel icon={UserSettings01Icon} label="Resp. técnico" />
              <InlineOwnerEdit
                value={ini.tech_owner}
                onSave={async (v) => {
                  await api.put(`/initiatives/${id}`, {
                    data: { tech_owner: v },
                    reason: 'Cambio de responsable técnico',
                  });
                  load();
                }}>
                <OwnerMeta label="" value={ini.tech_owner} initiativeName={ini.name} />
              </InlineOwnerEdit>
            </div>
            <div>
              <MetaLabel icon={StethoscopeIcon} label="Resp. clínico" />
              <InlineOwnerEdit
                value={ini.clinical_owner}
                onSave={async (v) => {
                  await api.put(`/initiatives/${id}`, {
                    data: { clinical_owner: v },
                    reason: 'Cambio de responsable clínico',
                  });
                  load();
                }}>
                <OwnerMeta label="" value={ini.clinical_owner} initiativeName={ini.name} />
              </InlineOwnerEdit>
            </div>
            {/* Creador de la iniciativa — editable solo por administradores.
                El backend restringe created_by_id al rol admin; aquí replicamos
                esa regla y el patrón de doble clic de los demás responsables. */}
            <div>
              <MetaLabel icon={UserAccountIcon} label="Creador" />
              {can.admin ? (
                editingCreator ? (
                  <div className="flex items-center gap-1">
                    <div className="flex-1">
                      <UserPicker
                        value=""
                        placeholder="Buscar persona..."
                        testId="creator-picker"
                        autoOpen
                        onSelect={async (user) => {
                          try {
                            // El Creador debe ser un usuario real (created_by_id es una FK).
                            // Si la persona viene del directorio EntraID, primero se
                            // resuelve a un usuario local (upsert JIT) para obtener su id.
                            let userId = user.id;
                            if (user.fromDirectory) {
                              const { data: resolved } = await api.post('/users/upsert-from-directory', {
                                oid: user.oid || '',
                                name: user.name,
                                email: user.email,
                                title: user.title || '',
                                department: user.department || '',
                              });
                              userId = resolved.id;
                            }
                            await api.put(`/initiatives/${id}`, {
                              data: { created_by_id: userId },
                              reason: 'Cambio de creador de la iniciativa',
                            });
                            toast.success('Creador actualizado');
                            setEditingCreator(false);
                            load();
                          } catch (e) {
                            toast.error(apiError(e));
                          }
                        }}
                        onClear={() => setEditingCreator(false)}
                      />
                    </div>
                    {d.creator && (
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await api.put(`/initiatives/${id}`, {
                              data: { created_by_id: null },
                              reason: 'Se quitó el creador de la iniciativa',
                            });
                            toast.success('Creador actualizado');
                            setEditingCreator(false);
                            load();
                          } catch (e) {
                            toast.error(apiError(e));
                          }
                        }}
                        className="h-7 px-2 rounded text-xs text-destructive hover:bg-destructive/10 shrink-0"
                        aria-label="Quitar creador">
                        Quitar
                      </button>
                    )}
                  </div>
                ) : (
                  <p
                    className="text-sm mt-1 cursor-default group"
                    role="button"
                    tabIndex={0}
                    title="Doble clic para editar"
                    onDoubleClick={() => setEditingCreator(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') setEditingCreator(true);
                    }}
                    aria-label={`Editar creador: ${d.creator?.name || 'sin asignar'}`}>
                    {d.creator ? d.creator.name : <span className="text-muted-foreground">Sin asignar</span>}
                  </p>
                )
              ) : (
                <p className="text-sm mt-1">
                  {d.creator ? d.creator.name : <span className="text-muted-foreground">Sin asignar</span>}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ── Grupo: Clasificación y datos ── */}
        <div className="mt-5 pt-5 border-t border-border">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80 mb-3">
            Clasificación y datos
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-4 auto-rows-min items-start">
            <div>
              <MetaLabel icon={Building01Icon} label="Proveedor" />
              <InlineEdit
                value={ini.provider}
                onSave={async (v) => {
                  await api.put(`/initiatives/${id}`, { data: { provider: v }, reason: 'Cambio de proveedor' });
                  load();
                }}
                className="text-sm mt-1 font-medium"
              />
            </div>
            <div>
              <MetaLabel icon={Location01Icon} label="Región" />
              <InlineEdit
                value={ini.processing_region}
                onSave={async (v) => {
                  await api.put(`/initiatives/${id}`, { data: { processing_region: v }, reason: 'Cambio de región' });
                  load();
                }}
                className="text-sm mt-1 font-medium"
              />
            </div>
            <div>
              <MetaLabel icon={Tag01Icon} label="Clasificación" />
              <InlineSelect
                value={ini.data_classification}
                options={['Pública', 'Interna', 'Confidencial', 'Restringida']}
                onSave={async (v) => {
                  await api.put(`/initiatives/${id}`, {
                    data: { data_classification: v },
                    reason: 'Cambio de clasificación',
                  });
                  load();
                }}
              />
            </div>
            <div>
              <MetaLabel icon={UserMultipleIcon} label="Usuarios estimados" />
              <InlineEdit
                value={String(ini.estimated_users || '')}
                type="number"
                onSave={async (v) => {
                  await api.put(`/initiatives/${id}`, {
                    data: { estimated_users: Number(v) },
                    reason: 'Cambio usuarios estimados',
                  });
                  load();
                }}
                className="text-sm mt-1 font-medium"
              />
            </div>
            <div>
              <MetaLabel icon={UserGroupIcon} label="Datos personales" />
              <InlineToggle
                value={ini.personal_data}
                onSave={async (v) => {
                  await api.put(`/initiatives/${id}`, {
                    data: { personal_data: v },
                    reason: 'Cambio datos personales',
                  });
                  load();
                }}
              />
            </div>
            <div>
              <MetaLabel icon={Database01Icon} label="Datos clínicos" />
              <InlineToggle
                value={ini.clinical_data}
                onSave={async (v) => {
                  await api.put(`/initiatives/${id}`, {
                    data: { clinical_data: v },
                    reason: 'Cambio datos clínicos',
                  });
                  load();
                }}
              />
            </div>
          </div>
        </div>

        {/* ── Grupo: Fechas (celdas anchas para que el editor de fecha respire) ── */}
        <div className="mt-5 pt-5 border-t border-border">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80 mb-3">Fechas</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4 auto-rows-min items-start">
            <Meta icon={Clock01Icon} label="Creación" value={fmtDate(ini.created_at)} />
            <Meta icon={Time01Icon} label="Última actualización" value={fmtDate(ini.updated_at)} />
            <Meta icon={SecurityCheckIcon} label="Última evaluación" value={fmtDate(ini.last_assessment_date)} />
            <div>
              <MetaLabel icon={Calendar03Icon} label="Próxima revisión" />
              <InlineEdit
                value={ini.next_review_date ? ini.next_review_date.split('T')[0] : ''}
                type="date"
                onSave={async (v) => {
                  await api.put(`/initiatives/${id}`, {
                    data: { next_review_date: v || null },
                    reason: 'Cambio fecha próxima revisión',
                  });
                  load();
                }}
                className="text-sm mt-1 font-medium"
              />
            </div>
            <div>
              <MetaLabel icon={Calendar03Icon} label="Fecha comprometida" />
              <InlineEdit
                value={ini.next_milestone_date ? ini.next_milestone_date.split('T')[0] : ''}
                type="date"
                onSave={async (v) => {
                  await api.put(`/initiatives/${id}`, {
                    data: { next_milestone_date: v || null },
                    reason: 'Cambio fecha comprometida',
                  });
                  load();
                }}
                className="text-sm mt-1 font-medium"
              />
            </div>
          </div>
        </div>

        {/* ── Grupo: Modelo, volumen, valor y próxima acción (texto largo) ── */}
        <div className="mt-5 pt-5 border-t border-border">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80 mb-3">
            Modelo, volumen y valor
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 auto-rows-min items-start">
            <div>
              <MetaLabel icon={AiBrain01Icon} label="Modelo" />
              <InlineEdit
                value={ini.model}
                placeholder="Ej: GPT-4o"
                onSave={async (v) => {
                  await api.put(`/initiatives/${id}`, { data: { model: v }, reason: 'Cambio de modelo' });
                  load();
                }}
                className="text-sm mt-1 font-medium leading-relaxed"
              />
            </div>
            <div>
              <MetaLabel icon={ChartUpIcon} label="Volumen" />
              <InlineEdit
                value={ini.usage_volume}
                placeholder="~2.000 consultas/mes"
                onSave={async (v) => {
                  await api.put(`/initiatives/${id}`, { data: { usage_volume: v }, reason: 'Cambio volumen' });
                  load();
                }}
                className="text-sm mt-1 font-medium leading-relaxed"
              />
            </div>
            <div>
              <MetaLabel icon={Coins01Icon} label="Valor esperado" />
              <p className="text-sm mt-1 font-medium leading-relaxed">
                {ini.expected_value ? `${ini.expected_value} · ${ini.roi_estimate || '—'}` : '—'}
              </p>
            </div>
            <div>
              <MetaLabel icon={Task01Icon} label="Próxima acción" />
              <InlineEdit
                value={ini.next_action}
                placeholder="Describe el siguiente paso…"
                onSave={async (v) => {
                  await api.put(`/initiatives/${id}`, { data: { next_action: v }, reason: 'Cambio próxima acción' });
                  load();
                }}
                className="text-sm mt-1 font-medium leading-relaxed"
              />
            </div>
          </div>
        </div>

        {/* ── Integraciones ── */}
        <div className="mt-6 pt-5 border-t border-border" data-testid="integration-tags">
          <div className="flex items-center gap-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Integraciones</p>
            <IntegrationTags
              integrations={ini.integrations || []}
              readOnly={!can.edit}
              onSave={async (updated) => {
                await api.put(`/initiatives/${id}`, {
                  data: { integrations: updated },
                  reason: 'Cambio integraciones',
                });
                load();
              }}
            />
          </div>
        </div>
      </div>

      {/* ── Capacidades vinculadas ── */}
      {capabilities.length > 0 && (
        <Card className="shadow-card border-border" data-testid="linked-capabilities">
          <CardContent className="p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              Capacidades IA vinculadas
            </p>
            <div className="flex flex-wrap gap-2">
              {capabilities.map((cap) => (
                <Link
                  key={cap.id || cap.capability_id}
                  to="/capacidades"
                  className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-xs hover:border-primary/30 hover:bg-muted/50 transition-colors">
                  <Badge variant="outline" className="text-[10px]">
                    {cap.relationship_type || 'usa'}
                  </Badge>
                  <span className="font-medium">{cap.name || cap.capability_name}</span>
                  {cap.category && <span className="text-muted-foreground">· {cap.category}</span>}
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Relación con negocio ── */}
      {relationship && (
        <Card className="shadow-card border-border" data-testid="business-relationship">
          <CardContent className="p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              Relación con negocio
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
              <Meta label="Patrocinador" value={relationship.sponsor} />
              <Meta label="Responsable de producto" value={relationship.product_owner} />
              <Meta label="Último contacto" value={fmtDate(relationship.last_contact)} />
              <Meta label="Próxima reunión" value={fmtDate(relationship.next_meeting)} />
              <Meta label="Riesgo paralelo" value={relationship.parallel_risk} />
              <Meta label="Proveedor contactado" value={relationship.provider_contacted ? 'Sí' : 'No'} />
              <Meta label="Decisión pendiente" value={relationship.pending_decision} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Timeline indicators: Triage & Consolidation ── */}
      {(triage || ini.consolidation_check) && (
        <Card className="shadow-card border-border" data-testid="timeline-indicators">
          <CardContent className="p-5 space-y-4">
            {ini.consolidation_check && (
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <HugeiconsIcon icon={CheckmarkCircle02Icon} className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-xs font-semibold">Verificación de consolidación</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Revisada el {fmtDate(ini.consolidation_check)}
                    {ini.consolidation_result && ` · Resultado: ${ini.consolidation_result}`}
                  </p>
                </div>
              </div>
            )}
            {triage && (
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                  <HugeiconsIcon icon={SecurityCheckIcon} className="h-4 w-4 text-accent" />
                </div>
                <div>
                  <p className="text-xs font-semibold">Triage · {triage.result}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Evaluador: {triage.evaluator} · {fmtDate(triage.evaluated_at)}
                    {triage.preliminary_risk && ` · Riesgo preliminar: ${triage.preliminary_risk}`}
                  </p>
                  {triage.notes && <p className="text-xs text-muted-foreground mt-1">{triage.notes}</p>}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={onTabChange}>
        <TabsList className="bg-muted flex-wrap h-auto">
          {[
            ['evaluaciones', `Evaluaciones (${d.assessments.length})`],
            ['beneficios', `Beneficios (${parseList(d.initiative.expected_benefits).length})`],
            ['decisiones', `Decisiones (${d.decisions.length})`],
            ['controles', `Controles (${d.controls.length})`],
            ['evidencias', `Evidencias (${d.documents.filter((doc) => doc.status !== 'Eliminado').length})`],
            ['metricas', `Métricas (${d.metrics.length})`],
            ['hitos', `Hitos (${parseList(d.initiative.tracking_milestones).length})`],
            ['inventario', 'Inventario'],
            ['historial', 'Historial'],
          ].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="text-xs sm:text-sm">
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="evaluaciones" className="mt-5 space-y-4">
          {can.edit && (
            <div className="flex justify-end mb-3">
              <Button
                asChild
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="add-assessment-btn">
                <Link to={`/iniciativas/${id}/evaluar`}>
                  <HugeiconsIcon icon={Add01Icon} className="h-4 w-4 mr-1.5" />
                  {d.assessments.length === 0 ? 'Evaluar riesgo' : 'Nueva evaluación'}
                </Link>
              </Button>
            </div>
          )}
          {d.assessments.length === 0 && (
            <EmptyState
              icon={SecurityCheckIcon}
              title="Sin evaluaciones de riesgo"
              subtitle={
                <EmptyExplainer
                  what="La evaluación mide el riesgo de esta iniciativa de IA en 15 dimensiones (datos, sesgo, seguridad, impacto clínico, cumplimiento y más). De ahí sale el riesgo inherente y el residual."
                  whyItMatters="Es la base de toda la gobernanza: define qué controles son obligatorios, quién debe revisar y con qué frecuencia hay que reevaluar. Sin ella no sabemos si la IA es segura de usar."
                  ifEmpty="La iniciativa no puede avanzar al Comité ni pasar a producción. Estaríamos usando IA sin conocer sus riesgos, lo que expone a la organización y a los pacientes."
                  whenDone="Obtenemos un puntaje de riesgo claro, controles recomendados y una ruta de aprobación. Así usamos la IA de forma responsable y con respaldo."
                />
              }
            />
          )}
          {d.assessments.map((a) => (
            <Card key={a.id} className="shadow-card border-border">
              <CardContent className="p-5">
                <div className="flex flex-wrap items-center gap-3 justify-between">
                  <div>
                    <p className="font-display font-semibold text-sm">
                      Versión {a.version} · {fmtDate(a.date)}
                    </p>
                    <p className="text-xs text-muted-foreground">Evaluador: {a.evaluator}</p>
                  </div>
                  <div className="flex gap-2 items-center">
                    <span className="text-xs text-muted-foreground">Inherente</span>
                    <RiskBadge level={a.inherent_level} />
                    <span className="text-xs text-muted-foreground">Residual</span>
                    <RiskBadge level={a.residual_level} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mt-4 text-xs">
                  <div>
                    <p className="font-semibold uppercase tracking-wider text-muted-foreground">Controles requeridos</p>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {a.required_controls.map((c) => (
                        <span key={c} className="px-2 py-0.5 rounded bg-secondary text-secondary-foreground">
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <p className="font-semibold uppercase tracking-wider text-muted-foreground">
                      Revisiones requeridas
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        ['dpo', 'DPO'],
                        ['clinical', 'Clínica'],
                        ['security', 'Seguridad'],
                        ['committee', 'Comité de IA'],
                      ].map(([k, l]) => {
                        const reviewer = a.reviewers?.[k];
                        const isActive = a.flags?.[k];
                        const reviewStatus = d.review_statuses?.[k];

                        // Determine icon based on review status
                        let flagIcon = CancelCircleIcon;
                        let flagColor = 'text-muted-foreground/50';
                        if (isActive) {
                          if (reviewStatus === 'Completada') {
                            flagIcon = CheckmarkCircle02Icon;
                            flagColor = 'text-success';
                          } else if (reviewStatus === 'Cancelada') {
                            flagIcon = CancelCircleIcon;
                            flagColor = 'text-destructive/60';
                          } else {
                            // Pendiente or En curso
                            flagIcon = Clock01Icon;
                            flagColor = 'text-warning';
                          }
                        }

                        return (
                          <HoverCard key={k} openDelay={200} closeDelay={100}>
                            <HoverCardTrigger asChild>
                              <span className="flex items-center gap-1 cursor-default">
                                <HugeiconsIcon icon={flagIcon} className={cn('h-3.5 w-3.5', flagColor)} />
                                {l}
                              </span>
                            </HoverCardTrigger>
                            {isActive && reviewer && (
                              <HoverCardContent className="w-72 p-4" align="start">
                                <div className="space-y-3">
                                  <div className="flex items-center gap-3">
                                    <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0">
                                      {reviewer.name?.slice(0, 2).toUpperCase() || '??'}
                                    </div>
                                    <div className="min-w-0">
                                      <p className="text-sm font-semibold truncate">{reviewer.name}</p>
                                      <p className="text-xs text-muted-foreground truncate">{reviewer.email}</p>
                                    </div>
                                  </div>
                                  {reviewer.assigned_at && (
                                    <p className="text-[11px] text-muted-foreground">
                                      Asignado el {fmtDate(reviewer.assigned_at)}
                                    </p>
                                  )}
                                  <div className="flex gap-2 pt-1">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 text-xs gap-1.5 flex-1"
                                      onClick={() =>
                                        window.open(
                                          `mailto:${reviewer.email}?subject=Revisión pendiente: ${ini.name}`,
                                          '_blank',
                                        )
                                      }
                                      aria-label={`Enviar correo a ${reviewer.name}`}>
                                      <HugeiconsIcon icon={Mail01Icon} className="h-3 w-3" />
                                      Correo
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 text-xs gap-1.5 flex-1"
                                      onClick={async () => {
                                        try {
                                          await api.post('/notifications', {
                                            user_id: reviewer.user_id,
                                            title: `Recordatorio: ${l} pendiente`,
                                            body: `La iniciativa ${ini.name} requiere su revisión (${l})`,
                                            type: 'reminder',
                                          });
                                          toast.success(`Recordatorio enviado a ${reviewer.name}`);
                                        } catch (e) {
                                          toast.error(apiError(e));
                                        }
                                      }}
                                      aria-label={`Enviar recordatorio a ${reviewer.name}`}>
                                      <HugeiconsIcon icon={Notification01Icon} className="h-3 w-3" />
                                      Recordatorio
                                    </Button>
                                  </div>
                                </div>
                              </HoverCardContent>
                            )}
                          </HoverCard>
                        );
                      })}
                    </div>
                    {a.diff_from_previous && (
                      <p className="text-muted-foreground mt-1">
                        Diferencias vs versión anterior: {a.diff_from_previous}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="controles" className="mt-5">
          <div className="flex justify-end mb-3">
            {can.edit && catalogs && (
              <AssignControlDialog id={id} catalogs={catalogs} existing={d.controls} onSaved={load} />
            )}
          </div>
          {d.controls.length === 0 ? (
            <EmptyState
              icon={SecurityCheckIcon}
              title="Sin controles asignados"
              subtitle={
                <EmptyExplainer
                  what="Los controles son las medidas concretas que mantienen la IA bajo control: revisión humana, límites de uso, protección de datos, monitoreo de sesgos, planes de respaldo, entre otros. Cada uno mitiga un riesgo detectado en la evaluación."
                  whyItMatters="Son la diferencia entre usar IA a ciegas y usarla con resguardos. Convierten el riesgo identificado en acciones verificables y asignan a un responsable de que se cumplan."
                  ifEmpty="Los riesgos quedan sin mitigar y sin dueño. La iniciativa no puede enviarse al Comité y, si llegara a operar, lo haría sin salvaguardas ante errores, fugas de datos o decisiones injustas."
                  whenDone="Cada riesgo relevante queda cubierto por una medida con responsable y estado de avance. Damos garantías de que la IA opera de forma segura y responsable, y habilitamos la aprobación."
                />
              }
            />
          ) : (
            <div className="bg-card rounded-xl border border-border shadow-card overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    {[
                      'Control',
                      'Categoría',
                      'Responsable',
                      'Estado',
                      'Fecha límite',
                      'Validación',
                      'Documentos vinculados',
                      '',
                    ].map((h) => (
                      <TableHead key={h} className="text-xs uppercase tracking-wide whitespace-nowrap">
                        {h}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d.controls.map((c) => (
                    <TableRow key={c.id} data-testid={`control-row-${c.control_id}`}>
                      <TableCell className="font-medium text-sm">{c.name || c.control_name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{c.category}</TableCell>
                      <TableCell className="text-sm">
                        {(() => {
                          const match = c.owner?.match(/^(.+?)\s*<(.+?)>$/);
                          const name = match ? match[1] : c.owner;
                          const email = match ? match[2] : null;
                          if (!email) return name || '—';
                          return (
                            <HoverCard openDelay={200} closeDelay={100}>
                              <HoverCardTrigger asChild>
                                <span className="cursor-default underline decoration-dotted decoration-muted-foreground/40 underline-offset-2">
                                  {name}
                                </span>
                              </HoverCardTrigger>
                              <HoverCardContent className="w-72 p-4" align="start">
                                <div className="space-y-3">
                                  <div className="flex items-center gap-3">
                                    <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0">
                                      {name.slice(0, 2).toUpperCase()}
                                    </div>
                                    <div className="min-w-0">
                                      <p className="text-sm font-semibold truncate">{name}</p>
                                      <p className="text-xs text-muted-foreground truncate">{email}</p>
                                    </div>
                                  </div>
                                  {c.created_at && (
                                    <p className="text-[11px] text-muted-foreground">
                                      Asignado el {fmtDate(c.created_at)}
                                    </p>
                                  )}
                                  <div className="flex gap-2 pt-1">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 text-xs gap-1.5 flex-1"
                                      onClick={() =>
                                        window.open(
                                          `mailto:${email}?subject=Control pendiente: ${c.name || c.control_name}`,
                                          '_blank',
                                        )
                                      }>
                                      <HugeiconsIcon icon={Mail01Icon} className="h-3 w-3" /> Correo
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 text-xs gap-1.5 flex-1"
                                      onClick={async () => {
                                        try {
                                          await api.post('/notifications', {
                                            user_id: c.owner_id || '',
                                            title: `Recordatorio: ${c.name || c.control_name}`,
                                            body: `El control ${c.name || c.control_name} para ${ini.name} está pendiente de implementación`,
                                            type: 'reminder',
                                          });
                                          toast.success(`Recordatorio enviado a ${name}`);
                                        } catch {
                                          toast.error('No se pudo enviar el recordatorio');
                                        }
                                      }}>
                                      <HugeiconsIcon icon={Notification01Icon} className="h-3 w-3" /> Recordatorio
                                    </Button>
                                  </div>
                                </div>
                              </HoverCardContent>
                            </HoverCard>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={c.status} />
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {fmtDate(c.due_date)}
                      </TableCell>
                      <TableCell className="text-xs">
                        {c.validation_result ? (
                          <div className="flex flex-col leading-tight">
                            <span>{c.validation_result}</span>
                            {c.implemented_at && (
                              <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                                {fmtDate(c.implemented_at)}
                              </span>
                            )}
                          </div>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        <ControlDocuments control={c} documents={d.documents} canEdit={can.edit} onSaved={load} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 justify-end">
                          {can.edit && c.status !== 'Implementado' && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs border-border"
                                  data-testid={`implement-control-${c.control_id}`}>
                                  Marcar implementado
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="font-display">
                                    Marcar control como implementado
                                  </AlertDialogTitle>
                                  <AlertDialogDescription asChild>
                                    <div className="space-y-2 text-sm text-muted-foreground">
                                      <p>
                                        Va a marcar el control{' '}
                                        <span className="font-medium text-foreground">{c.name || c.control_name}</span>{' '}
                                        como <span className="font-medium text-foreground">Implementado</span>. Al
                                        confirmar:
                                      </p>
                                      <ul className="list-disc space-y-1 pl-5">
                                        <li>El estado del control pasa a “Implementado”.</li>
                                        <li>Su validación se registra como “Conforme” con la fecha de hoy.</li>
                                        <li>El control cuenta como cumplido en el dossier de la iniciativa.</li>
                                        <li>La acción queda registrada en la auditoría.</li>
                                      </ul>
                                      <p>
                                        Verifique que las evidencias que respaldan el control ya están vinculadas antes
                                        de continuar.
                                      </p>
                                    </div>
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={async () => {
                                      try {
                                        await api.put(`/initiative-controls/${c.id}`, {
                                          status: 'Implementado',
                                          validation_result: 'Conforme',
                                        });
                                        toast.success('Control marcado como implementado');
                                        load();
                                      } catch (e) {
                                        toast.error(apiError(e));
                                      }
                                    }}>
                                    Marcar implementado
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                          {can.edit && (
                            <ControlDocuments
                              control={c}
                              documents={d.documents}
                              canEdit={can.edit}
                              onSaved={load}
                              variant="attach-button"
                            />
                          )}
                          {can.delete && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                  data-testid={`delete-control-${c.control_id}`}
                                  aria-label={`Desasignar control ${c.name || c.control_name}`}
                                  title="Desasignar control">
                                  <HugeiconsIcon icon={Delete02Icon} className="h-3.5 w-3.5" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="font-display">Desasignar control</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    ¿Desea quitar el control{' '}
                                    <span className="font-medium text-foreground">{c.name || c.control_name}</span> de
                                    esta iniciativa? El control dejará de contar para el dossier. Esta acción queda
                                    registrada en la auditoría.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    onClick={async () => {
                                      try {
                                        await api.delete(`/initiative-controls/${c.id}`);
                                        toast.success('Control desasignado');
                                        load();
                                      } catch (e) {
                                        toast.error(apiError(e));
                                      }
                                    }}>
                                    Desasignar
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="evidencias" className="mt-5">
          <div className="flex justify-end mb-3">{can.edit && <AddDocumentDialog id={id} onSaved={load} />}</div>
          {d.documents.filter((doc) => doc.status !== 'Eliminado').length === 0 ? (
            <EmptyState
              icon={FileAttachmentIcon}
              title="Sin evidencias adjuntas"
              subtitle={
                <EmptyExplainer
                  what="Las evidencias son los documentos que respaldan la iniciativa: contrato con el proveedor, política de datos, ficha del modelo, pruebas realizadas, consentimientos o informes técnicos."
                  whyItMatters="Demuestran que lo declarado es real y verificable. Son lo que el Comité, auditoría y los reguladores revisan para confiar en la iniciativa."
                  ifEmpty="El dossier queda incompleto y la iniciativa no puede enviarse al Comité. Ante una auditoría o un incidente no tendríamos cómo respaldar las decisiones tomadas."
                  whenDone="Dejamos trazabilidad completa y auditable. Cualquier persona puede entender y verificar cómo y con qué respaldo se aprobó el uso de esta IA."
                />
              }
            />
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {d.documents
                .filter((doc) => doc.status !== 'Eliminado')
                .map((doc) => (
                  <Card
                    key={doc.id}
                    className="shadow-card border-border group hover:border-primary/20 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-xl bg-primary/8 text-primary flex items-center justify-center shrink-0">
                          <HugeiconsIcon icon={FileAttachmentIcon} className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold leading-snug truncate">{doc.title}</p>
                            <StatusBadge status={doc.status} />
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {doc.type} · v{doc.version} · {doc.uploaded_by} · {fmtDate(doc.created_at)}
                          </p>
                          {doc.file_name && (
                            <p className="text-xs text-muted-foreground/70 mt-0.5 truncate">
                              {doc.file_name}
                              {doc.file_size > 0 && (
                                <span className="ml-1">
                                  (
                                  {doc.file_size >= 1024 * 1024
                                    ? `${(doc.file_size / (1024 * 1024)).toFixed(1)} MB`
                                    : `${Math.ceil(doc.file_size / 1024)} KB`}
                                  )
                                </span>
                              )}
                            </p>
                          )}
                          <div className="mt-2 text-xs">
                            <span className="font-medium">Controles asociados</span>
                            {d.controls.filter((c) => evidenceDocumentIds(c.evidence).includes(doc.id)).length === 0 ? (
                              <p className="text-muted-foreground">Sin asociación</p>
                            ) : (
                              <ul className="list-disc pl-4">
                                {d.controls
                                  .filter((c) => evidenceDocumentIds(c.evidence).includes(doc.id))
                                  .map((c) => (
                                    <li key={c.id}>{c.name}</li>
                                  ))}
                              </ul>
                            )}
                          </div>
                          {doc.used_in_decision && (
                            <p className="text-[10px] text-accent font-medium mt-1.5 flex items-center gap-1">
                              <HugeiconsIcon icon={CheckmarkCircle02Icon} className="h-3 w-3" />
                              Vinculado a decisión · protegido
                            </p>
                          )}
                        </div>
                      </div>
                      {/* Actions row */}
                      <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-border/50">
                        {can.edit && !doc.used_in_decision && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs gap-1.5 text-muted-foreground hover:text-destructive"
                                data-testid={`delete-doc-${doc.id}`}
                                aria-label={`Eliminar ${doc.title}`}>
                                <HugeiconsIcon icon={Delete02Icon} className="h-3.5 w-3.5" />
                                Eliminar
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle className="font-display">Eliminar documento</AlertDialogTitle>
                                <AlertDialogDescription>
                                  ¿Está seguro que desea eliminar{' '}
                                  <span className="font-medium text-foreground">{doc.title}</span>? Esta acción no se
                                  puede deshacer.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  onClick={async () => {
                                    try {
                                      await api.put(`/documents/${doc.id}`, { status: 'Eliminado' });
                                      toast.success('Documento eliminado');
                                      load();
                                    } catch (e) {
                                      toast.error(apiError(e));
                                    }
                                  }}>
                                  Eliminar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                        <div className="flex-1" />
                        {doc.file_key && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs gap-1.5 border-border"
                            onClick={async () => {
                              try {
                                const { data } = await api.post(`/documents/${doc.id}/download-url`);
                                window.open(data.download_url, '_blank');
                              } catch (e) {
                                toast.error(apiError(e));
                              }
                            }}
                            data-testid={`download-doc-${doc.id}`}
                            aria-label={`Descargar ${doc.file_name || doc.title}`}>
                            <HugeiconsIcon icon={Download01Icon} className="h-3.5 w-3.5" />
                            Descargar
                          </Button>
                        )}
                        {can.edit && doc.status === 'En revisión' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs gap-1.5 border-destructive/30 text-destructive hover:bg-destructive/10"
                              onClick={async () => {
                                try {
                                  await api.put(`/documents/${doc.id}`, { status: 'Rechazado' });
                                  toast.success('Documento rechazado');
                                  load();
                                } catch (e) {
                                  toast.error(apiError(e));
                                }
                              }}
                              data-testid={`reject-doc-${doc.id}`}>
                              <HugeiconsIcon icon={CancelCircleIcon} className="h-3.5 w-3.5" />
                              Rechazar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs gap-1.5 border-accent/30 text-accent hover:bg-accent/10"
                              onClick={async () => {
                                await api.put(`/documents/${doc.id}`, { status: 'Aprobado' });
                                toast.success('Documento aprobado');
                                load();
                              }}
                              data-testid={`approve-doc-${doc.id}`}>
                              <HugeiconsIcon icon={Tick02Icon} className="h-3.5 w-3.5" />
                              Aprobar
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="decisiones" className="mt-5 space-y-4">
          {d.decisions.length === 0 && (
            <EmptyState
              icon={WorkHistoryIcon}
              title="Sin decisiones del Comité"
              subtitle={
                <EmptyExplainer
                  what="Aquí quedan registradas las decisiones formales del Comité de IA: aprobar, aprobar con condiciones, postergar o rechazar la iniciativa, junto con quién votó y por qué."
                  whyItMatters="Es la autorización oficial para usar la IA. Deja claro quién asumió la responsabilidad de la decisión y bajo qué condiciones, en lugar de que cada área decida por su cuenta."
                  ifEmpty="Nadie ha autorizado formalmente esta iniciativa. Avanzar sin decisión del Comité significa usar IA sin respaldo institucional ni rendición de cuentas."
                  whenDone="Tenemos una autorización trazable con condiciones claras. La organización puede demostrar que la IA se aprobó mediante un proceso de gobernanza y no de forma improvisada."
                />
              }
            />
          )}
          {d.decisions.map((dec) => {
            const conditions = Array.isArray(dec.conditions)
              ? dec.conditions
              : String(dec.conditions || '')
                  .split(';')
                  .map((item) => item.trim())
                  .filter(Boolean);
            const votes = Array.isArray(dec.votes) ? dec.votes : [];
            return (
              <Card key={dec.id} className="shadow-card border-border">
                <CardContent className="p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <StatusBadge status={dec.status} />
                    <p className="text-xs text-muted-foreground">
                      {dec.decided_at
                        ? `Decidida el ${fmtDate(dec.decided_at)}`
                        : `En votación · quórum ${votes.length}/${dec.quorum_required}`}
                    </p>
                  </div>
                  {conditions.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Condiciones
                      </p>
                      <ul className="text-xs mt-1 space-y-1">
                        {conditions.map((c) => (
                          <li key={c} className="flex gap-1.5">
                            <HugeiconsIcon
                              icon={CheckmarkCircle02Icon}
                              className="h-3.5 w-3.5 text-risk-medium shrink-0 mt-0.5"
                            />
                            {c}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="mt-4 space-y-2">
                    {votes.map((v) => (
                      <div key={v.id} className="rounded-lg border border-border p-3">
                        <div className="flex flex-wrap justify-between gap-2">
                          <p className="text-xs font-semibold">
                            {v.user_name || v.voter}
                            {v.role && <span className="text-muted-foreground font-normal"> · {v.role}</span>}
                          </p>
                          <StatusBadge
                            status={
                              v.vote?.includes('Aprobar')
                                ? v.vote === 'Aprobar'
                                  ? 'Aprobada'
                                  : 'Aprobada con condiciones'
                                : v.vote === 'Rechazar'
                                  ? 'Rechazada'
                                  : 'En revisión'
                            }
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{v.justification}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        <TabsContent value="metricas" className="mt-5">
          <div className="flex justify-end mb-3">{can.edit && <AddMetricDialog id={id} onSaved={load} />}</div>
          {d.metrics.length === 0 ? (
            <EmptyState
              icon={WorkHistoryIcon}
              title="Sin métricas de valor"
              subtitle={
                <EmptyExplainer
                  what="Las métricas registran los resultados reales de la iniciativa: horas ahorradas, costos reducidos, calidad mejorada o volumen atendido, período a período."
                  whyItMatters="VIA existe para retornar valor a la organización de forma medible. Las métricas muestran si la IA cumple lo prometido o si solo genera costo y riesgo."
                  ifEmpty="No podemos comparar el valor esperado con el real ni justificar la inversión. La iniciativa opera sin saber si vale la pena mantenerla, escalarla o retirarla."
                  whenDone="Demostramos con datos el impacto de la IA y tomamos mejores decisiones sobre dónde invertir. Convertimos la promesa de valor en evidencia concreta."
                />
              }
            />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {d.metrics.map((m) => (
                <Card key={m.id} className="shadow-card border-border">
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">
                      {m.metric} · {m.period}
                    </p>
                    <p className="font-display text-2xl font-semibold mt-1 tabular">
                      {m.value} <span className="text-sm text-muted-foreground font-normal">{m.unit}</span>
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-2">
                      Meta: {m.target ?? '—'} · Anterior: {m.previous ?? '—'} · Fuente: {m.source}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="beneficios" className="mt-5">
          <BenefitsSection initiative={d.initiative} id={id} canEdit={can.edit} onSaved={load} />
        </TabsContent>

        <TabsContent value="hitos" className="mt-5">
          <MilestonesSection initiative={d.initiative} id={id} canEdit={can.edit} onSaved={load} />
        </TabsContent>

        <TabsContent value="inventario" className="mt-5 space-y-4">
          {/* Clasificación de IA (matriz de 4 cuadrantes) y alcance de gobernanza */}
          <Card className="shadow-card">
            <CardContent className="p-5">
              <p className="text-sm font-display font-semibold mb-4">Clasificación de IA</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
                <div>
                  <MetaLabel icon={AiBrain01Icon} label="Usa IA" />
                  {can.edit ? (
                    <InlineToggle
                      value={ini.uses_ai}
                      onSave={async (v) => {
                        await api.put(`/initiatives/${id}`, {
                          data: {
                            uses_ai: v,
                            governance_scope: v ? 'gobernanza_ia' : 'solo_inventario',
                          },
                          reason: 'Cambio de clasificación: uso de IA',
                        });
                        load();
                      }}
                    />
                  ) : (
                    <p className="text-sm mt-0.5 font-medium">{ini.uses_ai ? 'Sí' : 'No'}</p>
                  )}
                </div>
                <div>
                  <MetaLabel icon={AiBrain01Icon} label="Creado con IA" />
                  {can.edit ? (
                    <InlineToggle
                      value={ini.created_with_ai}
                      onSave={async (v) => {
                        await api.put(`/initiatives/${id}`, {
                          data: { created_with_ai: v },
                          reason: 'Cambio de clasificación: creado con IA',
                        });
                        load();
                      }}
                    />
                  ) : (
                    <p className="text-sm mt-0.5 font-medium">{ini.created_with_ai ? 'Sí' : 'No'}</p>
                  )}
                </div>
                <div>
                  <MetaLabel icon={SecurityCheckIcon} label="Alcance del registro" />
                  {can.edit ? (
                    <InlineSelect
                      value={ini.governance_scope === 'solo_inventario' ? 'Solo inventario' : 'Gobernanza de IA'}
                      options={['Gobernanza de IA', 'Solo inventario']}
                      onSave={async (v) => {
                        await api.put(`/initiatives/${id}`, {
                          data: { governance_scope: v === 'Solo inventario' ? 'solo_inventario' : 'gobernanza_ia' },
                          reason: 'Cambio de alcance de gobernanza',
                        });
                        load();
                      }}
                    />
                  ) : (
                    <p className="text-sm mt-0.5 font-medium">
                      {ini.governance_scope === 'solo_inventario' ? 'Solo inventario' : 'Gobernanza de IA'}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Inventario de la aplicación */}
          <Card className="shadow-card">
            <CardContent className="p-5">
              <p className="text-sm font-display font-semibold mb-4">Inventario de la aplicación</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
                <div>
                  <MetaLabel icon={ChartUpIcon} label="Costo de desarrollo (CLP)" />
                  {can.edit ? (
                    <InlineEdit
                      value={ini.development_cost_clp != null ? String(ini.development_cost_clp) : ''}
                      type="number"
                      placeholder="Ej: 12000000"
                      onSave={async (v) => {
                        const n = String(v).replace(/[^\d]/g, '');
                        await api.put(`/initiatives/${id}`, {
                          data: { development_cost_clp: n ? Number(n) : null },
                          reason: 'Actualización de costo de desarrollo',
                        });
                        load();
                      }}
                    />
                  ) : (
                    <p className="text-sm mt-0.5 font-medium">
                      {ini.development_cost_clp != null ? ini.development_cost_clp.toLocaleString('es-CL') : '—'}
                    </p>
                  )}
                </div>
                <div>
                  <MetaLabel icon={Calendar03Icon} label="Fecha de desarrollo" />
                  {can.edit ? (
                    <InlineEdit
                      value={ini.development_date ? ini.development_date.split('T')[0] : ''}
                      type="date"
                      onSave={async (v) => {
                        await api.put(`/initiatives/${id}`, {
                          data: { development_date: v || null },
                          reason: 'Actualización de fecha de desarrollo',
                        });
                        load();
                      }}
                    />
                  ) : (
                    <p className="text-sm mt-0.5 font-medium">{fmtDate(ini.development_date)}</p>
                  )}
                </div>
                <div>
                  <MetaLabel icon={UserAccountIcon} label="Desarrollado por" />
                  {can.edit ? (
                    <InlineEdit
                      value={ini.developed_by || ''}
                      placeholder="Equipo interno o desarrollador"
                      onSave={async (v) => {
                        await api.put(`/initiatives/${id}`, {
                          data: { developed_by: v || null },
                          reason: 'Actualización de desarrollador',
                        });
                        load();
                      }}
                    />
                  ) : (
                    <p className="text-sm mt-0.5 font-medium">{ini.developed_by || '—'}</p>
                  )}
                </div>
                <div>
                  <MetaLabel icon={Location01Icon} label="Alojamiento" />
                  {can.edit ? (
                    <InlineEdit
                      value={ini.hosting_location || ''}
                      placeholder="Ej: AWS, on-premise"
                      onSave={async (v) => {
                        await api.put(`/initiatives/${id}`, {
                          data: { hosting_location: v || null },
                          reason: 'Actualización de alojamiento',
                        });
                        load();
                      }}
                    />
                  ) : (
                    <p className="text-sm mt-0.5 font-medium">{ini.hosting_location || '—'}</p>
                  )}
                </div>
                <div>
                  <MetaLabel icon={Database01Icon} label="Base de datos" />
                  {can.edit ? (
                    <InlineEdit
                      value={ini.database_used || ''}
                      placeholder="Ej: PostgreSQL, DynamoDB"
                      onSave={async (v) => {
                        await api.put(`/initiatives/${id}`, {
                          data: { database_used: v || null },
                          reason: 'Actualización de base de datos',
                        });
                        load();
                      }}
                    />
                  ) : (
                    <p className="text-sm mt-0.5 font-medium">{ini.database_used || '—'}</p>
                  )}
                </div>
                <div>
                  <MetaLabel icon={Building01Icon} label="Proveedor externo" />
                  {can.edit ? (
                    <InlineToggle
                      value={ini.is_external_provider}
                      onSave={async (v) => {
                        await api.put(`/initiatives/${id}`, {
                          data: { is_external_provider: v },
                          reason: 'Cambio de proveedor externo',
                        });
                        load();
                      }}
                    />
                  ) : (
                    <p className="text-sm mt-0.5 font-medium">{ini.is_external_provider ? 'Sí' : 'No'}</p>
                  )}
                </div>
                {ini.is_external_provider && (
                  <div>
                    <MetaLabel icon={Building01Icon} label="Nombre del proveedor" />
                    {can.edit ? (
                      <InlineEdit
                        value={ini.external_provider_name || ''}
                        placeholder="Ej: Proveedor S.A."
                        onSave={async (v) => {
                          await api.put(`/initiatives/${id}`, {
                            data: { external_provider_name: v || null },
                            reason: 'Actualización de proveedor externo',
                          });
                          load();
                        }}
                      />
                    ) : (
                      <p className="text-sm mt-0.5 font-medium">{ini.external_provider_name || '—'}</p>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-4">
                <MetaLabel icon={Task01Icon} label="Motivo del desarrollo" />
                {can.edit ? (
                  <InlineEdit
                    value={ini.development_reason || ''}
                    placeholder="Por qué se desarrolló"
                    onSave={async (v) => {
                      await api.put(`/initiatives/${id}`, {
                        data: { development_reason: v || null },
                        reason: 'Actualización de motivo de desarrollo',
                      });
                      load();
                    }}
                  />
                ) : (
                  <p className="text-sm mt-0.5 font-medium">{ini.development_reason || '—'}</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="historial" className="mt-5 space-y-4">
          {d.shadow_origin && (
            <Card className="shadow-card border-risk-medhigh/30 bg-risk-medhigh/5">
              <CardContent className="p-4 flex items-start gap-3">
                <HugeiconsIcon icon={Radar01Icon} className="h-4 w-4 text-risk-medhigh mt-0.5" />
                <div className="text-xs">
                  <p className="font-semibold">Trazabilidad de origen · Shadow AI</p>
                  <p className="text-muted-foreground mt-1">
                    Detección: {d.shadow_origin.app_name} · Fuente: {d.shadow_origin.source} ·{' '}
                    {d.shadow_origin.observed_users} usuarios observados · Riesgo estimado{' '}
                    {d.shadow_origin.estimated_risk}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
          {d.reevaluations.length > 0 && (
            <Card className="shadow-card border-border">
              <CardContent className="p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  Reevaluaciones
                </p>
                {d.reevaluations.map((r) => (
                  <div
                    key={r.id}
                    className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-border last:border-0 text-xs">
                    <span>
                      {r.trigger} · {r.reason}
                    </span>
                    <div className="flex items-center gap-2">
                      <RiskBadge level={r.previous_risk} /> → <RiskBadge level={r.estimated_new_risk} />
                      <StatusBadge status={r.status} />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {(() => {
            // Build unified timeline from all data sources
            const FIELD_LABELS = {
              stage: 'Etapa',
              residual_risk: 'Riesgo residual',
              inherent_risk: 'Riesgo inherente',
              business_owner: 'Resp. de negocio',
              tech_owner: 'Resp. técnico',
              clinical_owner: 'Resp. clínico',
              next_action: 'Próxima acción',
              model_version: 'Versión del modelo',
              usage_volume: 'Volumen',
              committee_status: 'Estado comité',
              evaluation_status: 'Estado evaluación',
              provider: 'Proveedor',
              department: 'Departamento',
              description: 'Descripción',
              data_classification: 'Clasificación',
              processing_region: 'Región',
            };
            const timeline = [];

            // History entries (field changes)
            d.history.forEach((h) => {
              const before = h.before_data || h.before || {};
              const after = h.after_data || h.after || {};
              const changes = Object.keys(after)
                .filter((k) => k !== 'updated_at' && k !== 'created_at')
                .map((k) => ({
                  field: FIELD_LABELS[k] || k,
                  from: before[k] ?? '—',
                  to: after[k] ?? '—',
                }));
              timeline.push({
                type: 'change',
                timestamp: h.timestamp,
                user: h.user_name || h.user,
                reason: h.reason,
                changes,
              });
            });

            // Assessments as events
            d.assessments.forEach((a) => {
              timeline.push({
                type: 'assessment',
                timestamp: a.date || a.created_at,
                user: a.evaluator,
                detail: `Evaluación v${a.version} · Riesgo inherente ${a.inherent_level} → Residual ${a.residual_level}`,
              });
            });

            // Controls assigned
            d.controls.forEach((c) => {
              timeline.push({
                type: 'control',
                timestamp: c.created_at,
                user: c.owner,
                detail: `Control asignado: ${c.control_name || c.name} (${c.category})`,
                status: c.status,
              });
            });

            // Documents
            d.documents.forEach((doc) => {
              timeline.push({
                type: 'document',
                timestamp: doc.created_at,
                user: doc.uploaded_by,
                detail: `Documento: ${doc.title}`,
                status: doc.status,
                fileName: doc.file_name,
              });
            });

            // Metrics
            d.metrics.forEach((m) => {
              timeline.push({
                type: 'metric',
                timestamp: m.created_at,
                user: null,
                detail: `Métrica registrada: ${m.metric} = ${m.value} ${m.unit}`,
              });
            });

            // Sort descending (newest first)
            timeline.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

            if (timeline.length === 0) {
              return (
                <EmptyState
                  icon={WorkHistoryIcon}
                  title="Sin actividad registrada"
                  subtitle={
                    <EmptyExplainer
                      what="El historial es la bitácora automática de la iniciativa: cada cambio, evaluación, control, documento y decisión queda registrado con fecha y responsable."
                      whyItMatters="Es la memoria y la trazabilidad de la gobernanza. Permite entender cómo evolucionó la iniciativa y responder ante auditorías o incidentes."
                      ifEmpty="Aún no se ha registrado ninguna acción. A medida que trabajen sobre la iniciativa, el historial se construye solo; no hay que llenarlo a mano."
                      whenDone="Contamos con un registro completo y confiable de todo lo ocurrido, clave para auditar, aprender y mejorar el uso responsable de la IA."
                    />
                  }
                />
              );
            }

            const typeConfig = {
              change: { icon: PencilIcon, color: 'text-primary', bg: 'bg-primary/10', label: 'Cambio' },
              assessment: {
                icon: SecurityCheckIcon,
                color: 'text-risk-medium',
                bg: 'bg-risk-medium/10',
                label: 'Evaluación',
              },
              control: { icon: CheckmarkCircle02Icon, color: 'text-accent', bg: 'bg-accent/10', label: 'Control' },
              document: {
                icon: FileAttachmentIcon,
                color: 'text-secondary',
                bg: 'bg-secondary/10',
                label: 'Evidencia',
              },
              metric: { icon: WorkHistoryIcon, color: 'text-chart-1', bg: 'bg-chart-1/10', label: 'Métrica' },
            };

            return (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-5 top-0 bottom-0 w-px bg-border" aria-hidden="true" />
                <ol className="space-y-4" aria-label="Línea de tiempo de la iniciativa">
                  {timeline.map((event, idx) => {
                    const cfg = typeConfig[event.type];
                    return (
                      <li key={idx} className="relative pl-12">
                        {/* Dot */}
                        <div
                          className={cn(
                            'absolute left-3 top-1 h-5 w-5 rounded-full flex items-center justify-center',
                            cfg.bg,
                          )}>
                          <HugeiconsIcon icon={cfg.icon} className={cn('h-3 w-3', cfg.color)} />
                        </div>
                        {/* Content */}
                        <div className="bg-card rounded-lg border border-border p-3.5 shadow-sm">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2">
                              <Badge
                                variant="outline"
                                className={cn('text-[10px] px-1.5 py-0', cfg.color, 'border-current/20')}>
                                {cfg.label}
                              </Badge>
                              {event.user && <span className="text-xs font-medium">{event.user}</span>}
                            </div>
                            <time className="text-[11px] text-muted-foreground">{fmtDateTime(event.timestamp)}</time>
                          </div>

                          {event.reason && <p className="text-xs text-muted-foreground mt-1.5">{event.reason}</p>}

                          {event.type === 'change' && event.changes.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {event.changes.map((ch, i) => (
                                <div key={i} className="text-xs flex items-baseline gap-1.5 flex-wrap">
                                  <span className="font-medium text-muted-foreground">{ch.field}:</span>
                                  <span className="text-destructive/80 line-through">{String(ch.from)}</span>
                                  <span className="text-muted-foreground">→</span>
                                  <span className="text-accent font-medium">{String(ch.to)}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {event.type !== 'change' && event.detail && (
                            <p className="text-xs mt-1.5 font-medium">{event.detail}</p>
                          )}

                          {event.status && (
                            <div className="mt-1.5">
                              <StatusBadge status={event.status} />
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            );
          })()}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const EditDialog = ({ ini, catalogs, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const [reason, setReason] = useState('');
  const [tab, setTab] = useState('general');
  useEffect(() => {
    if (open)
      setF({
        business_owner: ini.business_owner || '',
        tech_owner: ini.tech_owner || '',
        clinical_owner: ini.clinical_owner || '',
        stage: ini.stage || '',
        residual_risk: ini.residual_risk || '',
        next_action: ini.next_action || '',
        usage_volume: ini.usage_volume || '',
        model_version: ini.model_version || '',
        provider: ini.provider || '',
        model: ini.model || '',
        department: ini.department || '',
        description: ini.description || '',
        processing_region: ini.processing_region || '',
        data_classification: ini.data_classification || '',
        estimated_users: ini.estimated_users || '',
        expected_value: ini.expected_value || '',
        roi_estimate: ini.roi_estimate || '',
        next_review_date: ini.next_review_date ? ini.next_review_date.split('T')[0] : '',
        integrations: (ini.integrations || []).join(', '),
        retention: ini.retention || '',
      });
  }, [open, ini]);
  const save = async () => {
    const payload = { ...f };
    if (typeof payload.integrations === 'string') {
      payload.integrations = payload.integrations
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }
    // estimated_users es una columna integer en la BD: nunca enviar '' (cadena
    // vacía) porque Postgres la rechaza (22P02). Vacío/no numérico -> 0.
    {
      const n = Number(payload.estimated_users);
      payload.estimated_users = Number.isFinite(n) && payload.estimated_users !== '' ? n : 0;
    }
    if (payload.next_review_date === '') delete payload.next_review_date;
    try {
      await api.put(`/initiatives/${ini.id}`, { data: payload, reason: reason || 'Actualización de ficha' });
      toast.success('Iniciativa actualizada · cambio registrado en historial');
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  if (!catalogs) return null;

  const Section = ({ title, children }) => (
    <div className="space-y-3">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{title}</p>
      {children}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-border" data-testid="edit-initiative-btn">
          <HugeiconsIcon icon={PencilIcon} className="h-4 w-4 mr-2" /> Editar
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display">Editar iniciativa</DialogTitle>
          <DialogDescription>
            Cada cambio queda registrado con valores antes/después en el historial y la auditoría.
          </DialogDescription>
        </DialogHeader>

        {/* Tabs for sections */}
        <div className="flex gap-1 border-b border-border pb-2 mb-2">
          {[
            ['general', 'General'],
            ['responsables', 'Responsables'],
            ['tecnologia', 'Tecnología'],
            ['operacion', 'Operación'],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={cn(
                'px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                tab === k ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground',
              )}>
              {label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto space-y-4 pr-1" style={{ maxHeight: '50vh' }}>
          {tab === 'general' && (
            <>
              <Section title="Información básica">
                <div className="space-y-1.5">
                  <Label className="text-xs">Descripción</Label>
                  <Textarea
                    rows={2}
                    value={f.description}
                    onChange={(e) => setF({ ...f, description: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Departamento</Label>
                    <Select value={f.department} onValueChange={(v) => setF({ ...f, department: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(catalogs.departments || []).map((d) => (
                          <SelectItem key={d} value={d}>
                            {d}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Etapa</Label>
                    <Select value={f.stage} onValueChange={(v) => setF({ ...f, stage: v })}>
                      <SelectTrigger data-testid="edit-stage-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {catalogs.stages.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Riesgo residual</Label>
                    <Select value={f.residual_risk} onValueChange={(v) => setF({ ...f, residual_risk: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {catalogs.risk_levels.map((r) => (
                          <SelectItem key={r} value={r}>
                            {r}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Valor esperado</Label>
                    <Select value={f.expected_value} onValueChange={(v) => setF({ ...f, expected_value: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {['Bajo', 'Medio', 'Alto', 'Crítico'].map((v) => (
                          <SelectItem key={v} value={v}>
                            {v}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </Section>
              <Section title="Planificación">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Próxima acción</Label>
                    <Input value={f.next_action} onChange={(e) => setF({ ...f, next_action: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Próxima revisión</Label>
                    <Input
                      type="date"
                      value={f.next_review_date}
                      onChange={(e) => setF({ ...f, next_review_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">ROI estimado</Label>
                    <Input
                      value={f.roi_estimate}
                      onChange={(e) => setF({ ...f, roi_estimate: e.target.value })}
                      placeholder="-20% tiempo espera"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Retención</Label>
                    <Input value={f.retention} onChange={(e) => setF({ ...f, retention: e.target.value })} />
                  </div>
                </div>
              </Section>
            </>
          )}

          {tab === 'responsables' && (
            <Section title="Responsables de la iniciativa">
              <div className="grid grid-cols-1 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Resp. de negocio</Label>
                  <UserPicker
                    value={f.business_owner}
                    onSelect={(user) => setF({ ...f, business_owner: `${user.name} <${user.email}>` })}
                    onClear={() => setF({ ...f, business_owner: '' })}
                    placeholder="Buscar responsable de negocio..."
                    testId="edit-owner-picker"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Responsable técnico</Label>
                  <UserPicker
                    value={f.tech_owner}
                    onSelect={(user) => setF({ ...f, tech_owner: `${user.name} <${user.email}>` })}
                    onClear={() => setF({ ...f, tech_owner: '' })}
                    placeholder="Buscar responsable técnico..."
                    testId="edit-tech-owner-picker"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Responsable clínico</Label>
                  <UserPicker
                    value={f.clinical_owner}
                    onSelect={(user) => setF({ ...f, clinical_owner: `${user.name} <${user.email}>` })}
                    onClear={() => setF({ ...f, clinical_owner: '' })}
                    placeholder="Buscar responsable clínico..."
                    testId="edit-clinical-owner-picker"
                  />
                </div>
              </div>
            </Section>
          )}

          {tab === 'tecnologia' && (
            <Section title="Tecnología y proveedor">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Proveedor</Label>
                  <Input value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Modelo</Label>
                  <Input value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Versión del modelo</Label>
                  <Input value={f.model_version} onChange={(e) => setF({ ...f, model_version: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Región de procesamiento</Label>
                  <Input
                    value={f.processing_region}
                    onChange={(e) => setF({ ...f, processing_region: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Clasificación de datos</Label>
                  <Select value={f.data_classification} onValueChange={(v) => setF({ ...f, data_classification: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['Pública', 'Interna', 'Confidencial', 'Restringida'].map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Integraciones</Label>
                  <Input
                    value={f.integrations}
                    onChange={(e) => setF({ ...f, integrations: e.target.value })}
                    placeholder="SAP, Epic, PACS (separados por coma)"
                  />
                </div>
              </div>
            </Section>
          )}

          {tab === 'operacion' && (
            <Section title="Operación y uso">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Usuarios estimados</Label>
                  <Input
                    type="number"
                    value={f.estimated_users}
                    onChange={(e) => setF({ ...f, estimated_users: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Volumen de uso</Label>
                  <Input
                    value={f.usage_volume}
                    onChange={(e) => setF({ ...f, usage_volume: e.target.value })}
                    placeholder="~2.000 consultas/mes"
                  />
                </div>
              </div>
            </Section>
          )}
        </div>

        {/* Reason + save always visible */}
        <div className="space-y-3 pt-3 border-t border-border">
          <div className="space-y-1.5">
            <Label className="text-xs">Motivo del cambio</Label>
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Motivo para el registro de auditoría"
              data-testid="edit-reason-input"
            />
          </div>
          <DialogFooter>
            <Button
              onClick={save}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="edit-save-btn">
              Guardar cambios
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ReevalDialog = ({ ini, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [trigger, setTrigger] = useState(REEVAL_TRIGGERS[1]);
  const [reason, setReason] = useState('');
  const save = async () => {
    if (!reason) {
      toast.error('Indique el motivo de la reevaluación');
      return;
    }
    try {
      await api.post('/reevaluations', { initiative_id: ini.id, trigger, reason, priority: 'Alta' });
      toast.success('Reevaluación activada · el riesgo anterior queda conservado');
      setOpen(false);
      setReason('');
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-border" data-testid="trigger-reeval-btn">
          <HugeiconsIcon icon={Refresh01Icon} className="h-4 w-4 mr-2" /> Reevaluar
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display">Activar reevaluación</DialogTitle>
          <DialogDescription>
            Se conservará el riesgo anterior ({ini.residual_risk}) y se generará una nueva versión de evaluación.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Motivo gatillante</Label>
            <Select value={trigger} onValueChange={setTrigger}>
              <SelectTrigger data-testid="reeval-trigger-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REEVAL_TRIGGERS.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Descripción del cambio</Label>
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Describa el cambio que obliga a reevaluar"
              data-testid="reeval-reason-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={save}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="reeval-save-btn">
            Activar reevaluación
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AssignControlDialog = ({ id, catalogs, existing, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [controlId, setControlId] = useState('');
  const [owner, setOwner] = useState(null);
  const available = catalogs.controls.filter((c) => !existing.some((e) => e.control_id === c.id));
  const save = async () => {
    if (!controlId) return;
    try {
      await api.post(`/initiatives/${id}/controls`, {
        control_id: controlId,
        owner: owner ? `${owner.name} <${owner.email}>` : undefined,
        owner_id: owner?.id || undefined,
      });
      toast.success('Control asignado · tarea creada para el responsable');
      setOpen(false);
      setControlId('');
      setOwner(null);
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="assign-control-btn">
          <HugeiconsIcon icon={Add01Icon} className="h-4 w-4 mr-1.5" /> Asignar control
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display">Asignar control del catálogo</DialogTitle>
          <DialogDescription>
            Seleccione el control y asigne un responsable. Se creará una tarea en su bandeja automáticamente.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Control</Label>
            <Select value={controlId} onValueChange={setControlId}>
              <SelectTrigger data-testid="control-select">
                <SelectValue placeholder="Seleccione un control" />
              </SelectTrigger>
              <SelectContent>
                {available.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} · {c.category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Responsable de implementar</Label>
            <UserPicker
              value={owner?.name || ''}
              onSelect={setOwner}
              onClear={() => setOwner(null)}
              placeholder="Buscar responsable del control..."
              testId="control-owner-picker"
            />
            <p className="text-[10px] text-muted-foreground">Si no asigna un responsable, se le asignará a usted.</p>
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={save}
            disabled={!controlId}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="control-save-btn">
            Asignar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AddDocumentDialog = ({ id, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [version, setVersion] = useState('1.0');
  const [type, setType] = useState(DOC_TYPES[0]);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFiles = (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length > 10) {
      toast.error('Máximo 10 archivos por subida');
      return;
    }
    setFiles(selected);
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const save = async () => {
    if (!title.trim() || !version.trim()) {
      toast.error('Indique título y versión del documento');
      return;
    }
    if (files.length === 0) {
      toast.error('Seleccione al menos un archivo');
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      // 1. Request presigned URLs for all files in one batch call
      const filesMeta = files.map((f) => ({
        file_name: f.name,
        content_type: f.type || 'application/octet-stream',
        file_size: f.size,
      }));

      const { data: urlData } = await api.post(`/initiatives/${id}/upload-urls`, { doc_type: type, files: filesMeta });
      const urls = urlData.urls;

      // 2. Upload all files to S3 in parallel using presigned PUT URLs
      let completed = 0;
      const uploadPromises = urls.map((urlInfo, idx) => {
        const file = files[idx];
        return fetch(urlInfo.upload_url, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
          },
          body: file,
        }).then((res) => {
          if (!res.ok) throw new Error(`Error al subir ${file.name}`);
          completed++;
          setProgress(Math.round((completed / urls.length) * 100));
          return urlInfo;
        });
      });

      const uploaded = await Promise.all(uploadPromises);

      // 3. Register each document in the backend (metadata + file_key)
      const registerPromises = uploaded.map((urlInfo, idx) =>
        api.post(`/initiatives/${id}/documents`, {
          title: files.length === 1 ? title : `${title} · ${files[idx].name.replace(/\.[^.]+$/, '')}`,
          type,
          version: version.trim(),
          file_key: urlInfo.file_key,
          file_name: urlInfo.file_name,
          file_size: files[idx].size,
          content_type: files[idx].type || 'application/octet-stream',
        }),
      );

      await Promise.all(registerPromises);

      toast.success(
        files.length === 1
          ? 'Evidencia adjuntada · registrada en auditoría'
          : `${files.length} archivos adjuntados · registrados en auditoría`,
      );
      setOpen(false);
      setTitle('');
      setVersion('1.0');
      setFiles([]);
      setProgress(0);
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!uploading) setOpen(v);
      }}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="add-document-btn">
          <HugeiconsIcon icon={Add01Icon} className="h-4 w-4 mr-1.5" /> Adjuntar evidencia
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Adjuntar documento</DialogTitle>
          <DialogDescription>
            Seleccione archivos y complete los metadatos. Los archivos se suben directamente de forma segura.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="doc-title" className="text-xs">
              Título
            </Label>
            <Input
              id="doc-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ej: DPIA v1.0"
              data-testid="document-title-input"
              aria-label="Título del documento"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="doc-version">Versión documental</Label>
            <Input id="doc-version" value={version} onChange={(e) => setVersion(e.target.value)} maxLength={40} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="doc-type" className="text-xs">
              Tipo
            </Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="document-type-select" aria-label="Tipo de documento">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="doc-files" className="text-xs">
              Archivos
            </Label>
            <input
              id="doc-files"
              type="file"
              multiple
              onChange={handleFiles}
              className="block w-full text-xs file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-primary/8 file:text-primary hover:file:bg-primary/12 cursor-pointer"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.webp,.txt,.csv"
              data-testid="document-file-input"
              aria-label="Seleccionar archivos para adjuntar"
            />
            {files.length > 0 && (
              <ul className="mt-2 space-y-1" aria-label="Archivos seleccionados">
                {files.map((f, idx) => (
                  <li
                    key={`${f.name}-${idx}`}
                    className="flex items-center justify-between text-xs bg-muted/50 rounded-lg px-3 py-1.5">
                    <span className="truncate flex-1 mr-2">
                      <HugeiconsIcon
                        icon={FileAttachmentIcon}
                        className="h-3 w-3 inline mr-1.5 text-muted-foreground"
                      />
                      {f.name}
                      <span className="text-muted-foreground ml-1.5">({formatSize(f.size)})</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      className="text-muted-foreground hover:text-foreground shrink-0"
                      aria-label={`Quitar ${f.name}`}>
                      <HugeiconsIcon icon={CancelCircleIcon} className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {uploading && (
            <div
              className="space-y-1.5"
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300 rounded-full"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground text-center">Subiendo archivos... {progress}%</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            onClick={save}
            disabled={uploading}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="document-save-btn">
            {uploading ? 'Subiendo...' : 'Adjuntar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AddMetricDialog = ({ id, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    metric: METRIC_TYPES[0],
    value: '',
    unit: '',
    source: '',
    period: '2025-Q4',
    target: '',
  });
  const save = async () => {
    if (!f.value || !f.unit) {
      toast.error('Valor y unidad son obligatorios');
      return;
    }
    try {
      await api.post(`/initiatives/${id}/metrics`, {
        ...f,
        value: Number(f.value),
        target: f.target ? Number(f.target) : null,
      });
      toast.success('Métrica registrada');
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="add-metric-btn">
          <HugeiconsIcon icon={Add01Icon} className="h-4 w-4 mr-1.5" /> Registrar métrica
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display">Registrar métrica de valor</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs">Métrica</Label>
            <Select value={f.metric} onValueChange={(v) => setF({ ...f, metric: v })}>
              <SelectTrigger data-testid="metric-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METRIC_TYPES.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Valor</Label>
            <Input
              type="number"
              value={f.value}
              onChange={(e) => setF({ ...f, value: e.target.value })}
              data-testid="metric-value-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Unidad</Label>
            <Input
              value={f.unit}
              onChange={(e) => setF({ ...f, unit: e.target.value })}
              placeholder="hrs/semana, %, MM CLP"
              data-testid="metric-unit-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Fuente</Label>
            <Input value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Periodo</Label>
            <Input value={f.period} onChange={(e) => setF({ ...f, period: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Meta</Label>
            <Input type="number" value={f.target} onChange={(e) => setF({ ...f, target: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={save}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="metric-save-btn">
            Registrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
